import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { updateMetaTags } from '../utils/seo'

const SamsungDisplayReparaturPage: React.FC = () => {
  const navigate = useNavigate()
  const go = (path: string) => { navigate(path); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50) }

  useEffect(() => {
    updateMetaTags({
      title: 'Samsung Display Reparatur – Alle Modelle & Preise | PhoneMedic',
      description: 'Samsung Display Reparatur ✓ Galaxy S24 / S23 / S22 / A-Serie ✓ AMOLED & Super AMOLED ✓ ab 89€ ✓ 12 Monate Garantie ✓ Express 24h ✓ Bundesweiter Versand | PhoneMedic',
      keywords: ['Samsung Display Reparatur', 'Samsung Bildschirm kaputt', 'Samsung Display tauschen', 'Galaxy S24 Display', 'Galaxy S23 Display', 'Samsung AMOLED Reparatur', 'Samsung Glas gesprungen'],
      canonicalUrl: 'https://www.phone-medic.de/samsung-display-reparatur',
    })

    const schema = [
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'Samsung Display Reparatur',
        description: 'Professionelle Samsung Display Reparatur für alle Modelle. AMOLED, Super AMOLED, 12 Monate Garantie, Express 24h Service.',
        provider: { '@type': 'LocalBusiness', name: 'PhoneMedic', url: 'https://www.phone-medic.de', telephone: '+491605010907' },
        offers: [
          { '@type': 'Offer', name: 'Samsung Galaxy A34 Display', price: '89.00', priceCurrency: 'EUR', priceValidUntil: '2026-12-31' },
          { '@type': 'Offer', name: 'Samsung Galaxy S22 Display', price: '119.00', priceCurrency: 'EUR', priceValidUntil: '2026-12-31' },
          { '@type': 'Offer', name: 'Samsung Galaxy S24 Display', price: '149.00', priceCurrency: 'EUR', priceValidUntil: '2026-12-31' },
        ],
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          reviewCount: '32',
          bestRating: '5',
        },
        areaServed: 'DE',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Was kostet ein Samsung Display Tausch?',
            acceptedAnswer: { '@type': 'Answer', text: 'Die Preise variieren je nach Modell: Samsung Galaxy A-Serie ab 89€, Galaxy S22 ab 119€, Galaxy S23 ab 129€, Galaxy S24 ab 149€. Pro- und Ultra-Modelle sind etwas teurer. Alle Preise inklusive Einbau und 12 Monate Garantie.' },
          },
          {
            '@type': 'Question',
            name: 'Verliere ich Daten beim Samsung Display Tausch?',
            acceptedAnswer: { '@type': 'Answer', text: 'Nein. Beim Display-Tausch wird ausschließlich der Bildschirm ausgewechselt. Alle Daten, Apps und Einstellungen bleiben vollständig erhalten.' },
          },
          {
            '@type': 'Question',
            name: 'Wie lange dauert ein Samsung Display Tausch?',
            acceptedAnswer: { '@type': 'Answer', text: 'Der eigentliche Tausch dauert 45–90 Minuten. Inklusive Versandzeit rechne mit 3–5 Werktagen. Express-Service 24h auf Anfrage.' },
          },
          {
            '@type': 'Question',
            name: 'Repariert ihr auch Samsung Galaxy A-Serie?',
            acceptedAnswer: { '@type': 'Answer', text: 'Ja. Wir reparieren alle Samsung Galaxy Modelle – von der A-Serie (A14, A34, A54) über die S-Serie (S22, S23, S24) bis zu Fold- und Flip-Modellen.' },
          },
          {
            '@type': 'Question',
            name: 'Was ist, wenn nur das Glas gerissen ist?',
            acceptedAnswer: { '@type': 'Answer', text: 'Beim Samsung ist Glas und Display in der Regel verklebt und muss als Einheit getauscht werden. Nur-Glas-Tausch ist technisch riskant – wir empfehlen den kompletten Display-Tausch.' },
          },
        ],
      },
    ]

    let script = document.querySelector('#samsung-display-schema') as HTMLScriptElement
    if (!script) {
      script = document.createElement('script')
      script.id = 'samsung-display-schema'
      script.type = 'application/ld+json'
      document.head.appendChild(script)
    }
    script.textContent = JSON.stringify(schema)
    return () => { document.querySelector('#samsung-display-schema')?.remove() }
  }, [])

  const prices = [
    { model: 'Samsung Galaxy A14 / A24', price: 'ab 89 €' },
    { model: 'Samsung Galaxy A34 / A54', price: 'ab 99 €' },
    { model: 'Samsung Galaxy S22 / S22+', price: 'ab 119 €' },
    { model: 'Samsung Galaxy S22 Ultra', price: 'ab 139 €' },
    { model: 'Samsung Galaxy S23 / S23+', price: 'ab 129 €' },
    { model: 'Samsung Galaxy S23 Ultra', price: 'ab 149 €' },
    { model: 'Samsung Galaxy S24 / S24+', price: 'ab 149 €' },
    { model: 'Samsung Galaxy S24 Ultra', price: 'ab 169 €' },
    { model: 'Samsung Galaxy Fold / Flip', price: 'auf Anfrage' },
  ]

  const damages = [
    { title: 'Gesprungenes Glas', desc: 'Risse durch Sturz. Das Glas ist zerbrochen, aber das Display zeigt noch ein Bild. Tausch empfohlen um weitere Schäden durch eindringende Feuchtigkeit zu vermeiden.' },
    { title: 'Schwarzer Bildschirm', desc: 'Das Samsung reagiert und vibriert, der Bildschirm bleibt aber schwarz. Ein Zeichen für ein defektes AMOLED-Panel oder einen gelösten Displaystecker.' },
    { title: 'Farbfehler & Streifen', desc: 'Horizontale oder vertikale Streifen, verfärbte Bereiche oder flackernder Bildschirm – typische Symptome eines beschädigten AMOLED-Panels.' },
    { title: 'Touch funktioniert nicht', desc: 'Das Display zeigt ein Bild, reagiert aber nicht auf Berührungen. Kann ein Displayschaden oder ein Steckerproblem sein.' },
    { title: 'Flecken & Schatten', desc: 'Dunkle Flecken oder Schatten auf dem Display – häufig nach einem Sturz oder Druck auf das Display.' },
    { title: 'Wasserschaden am Display', desc: 'Feuchtigkeit unter dem Glas hinterlässt Beschläge oder Wolken. Das Display muss getauscht werden.' },
  ]

  const faqs = [
    { q: 'Was kostet ein Samsung Display Tausch?', a: 'A-Serie ab 89€, Galaxy S22 ab 119€, S23 ab 129€, S24 ab 149€. Pro- und Ultra-Modelle etwas mehr. Alle Preise inkl. Einbau und 12 Monate Garantie.' },
    { q: 'Verliere ich Daten beim Display-Tausch?', a: 'Nein. Beim Display-Tausch wird ausschließlich der Bildschirm gewechselt. Alle Daten bleiben erhalten.' },
    { q: 'Wie lange dauert ein Samsung Display Tausch?', a: '45–90 Minuten. Inklusive Versand 3–5 Werktage. Express 24h auf Anfrage.' },
    { q: 'Repariert ihr auch die Galaxy A-Serie?', a: 'Ja. Wir reparieren alle Samsung Galaxy Modelle – von der A-Serie über die S-Serie bis zu Fold- und Flip-Modellen.' },
    { q: 'Was ist, wenn nur das Glas gerissen ist?', a: 'Beim Samsung ist Glas und Display in der Regel verklebt und muss als Einheit getauscht werden. Nur-Glas-Tausch ist technisch riskant und wir empfehlen den kompletten Display-Tausch.' },
    { q: 'Welche Display-Qualität wird verbaut?', a: 'Wir verbauen Displays in hochwertiger AMOLED OEM-Qualität. Farbdarstellung, Helligkeit und Touch-Empfindlichkeit entsprechen dem Original.' },
  ]

  return (
    <div className="min-h-screen bg-white">
      <section
        className="relative text-white py-20"
        style={{ backgroundImage: 'url(/hero-bg.webp)', backgroundSize: 'cover', backgroundPosition: 'center top' }}
      >
        <div className="absolute inset-0 bg-gray-900/80" />
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-indigo-600/20 border border-indigo-500/30 text-indigo-300 text-sm font-medium px-3 py-1 rounded-full mb-6">
              <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full"></span>
              AMOLED · Super AMOLED · alle Modelle
            </div>
            <h1 className="text-5xl font-bold mb-6 leading-tight">
              Samsung Display Reparatur –<br />
              <span className="text-indigo-400">Alle Modelle & Preise</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl">
              Samsung-Glas gesprungen, schwarzer Bildschirm oder Touch reagiert nicht? Wir tauschen Displays für alle Galaxy Modelle – professionell, mit 12 Monaten Garantie, bundesweiter Einsende-Service.
            </p>
            <div className="flex flex-wrap gap-4">
              <button onClick={() => go('/repair')} className="bg-indigo-600 text-white px-7 py-3.5 rounded-lg font-semibold hover:bg-indigo-700 transition-colors">
                Modell wählen & Preis prüfen
              </button>
              <a href="mailto:service@phone-medic.de" className="bg-white/10 text-white border border-white/20 px-7 py-3.5 rounded-lg font-semibold hover:bg-white/20 transition-colors">
                Per E-Mail anfragen
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gray-800 text-white py-5 border-t border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {[
              { number: 'ab 89 €', label: 'Display-Tausch' },
              { number: '45–90 min', label: 'Reparaturzeit' },
              { number: '12 Monate', label: 'Garantie' },
              { number: 'Bundesweit', label: 'Einsende-Service' },
            ].map((s, i) => (
              <div key={i}>
                <div className="text-2xl font-bold text-indigo-400">{s.number}</div>
                <div className="text-sm text-gray-400">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Welche Displayschäden können wir beheben?</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {damages.map((d, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-bold text-gray-900 mb-2">{d.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Preise nach Modell</h2>
            <p className="text-gray-500">Alle Preise inklusive Einbau und 12 Monate Garantie</p>
          </div>
          <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
            <div className="grid grid-cols-2 bg-gray-800 text-white px-6 py-3 text-sm font-semibold">
              <span>Modell</span>
              <span className="text-right">Preis</span>
            </div>
            {prices.map((p, i) => (
              <div key={i} className={`grid grid-cols-2 px-6 py-4 text-sm border-b border-gray-200 last:border-0 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                <span className="text-gray-700 font-medium">{p.model}</span>
                <span className="text-right font-bold text-indigo-600">{p.price}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-3 text-center">Exakte Preise für dein Modell direkt im Reparatur-Konfigurator.</p>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-10">Häufige Fragen</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">{faq.q}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Weitere Samsung Services</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Samsung Akku tauschen', path: '/samsung-akku-tauschen' },
              { label: 'iPhone Display Reparatur', path: '/iphone-display-reparatur' },
              { label: 'iPhone Akku tauschen', path: '/iphone-akku-tauschen' },
              { label: 'Alle Reparaturen', path: '/repair' },
            ].map((item) => (
              <button key={item.path} onClick={() => go(item.path)} className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm font-medium text-gray-700 hover:border-indigo-400 hover:text-indigo-700 hover:bg-indigo-50 transition-colors text-center">
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-3">Samsung Display kaputt? Wir reparieren es.</h2>
          <p className="text-gray-400 mb-8">Alle Modelle · ab 89 € · 12 Monate Garantie · Bundesweiter Einsende-Service</p>
          <div className="flex flex-wrap justify-center gap-4">
            <button onClick={() => go('/repair')} className="bg-indigo-600 text-white px-7 py-3.5 rounded-lg font-semibold hover:bg-indigo-700 transition-colors">
              Modell auswählen & buchen
            </button>
            <a href="mailto:service@phone-medic.de" className="bg-white text-gray-900 px-7 py-3.5 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
              Per E-Mail anfragen
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}

export default SamsungDisplayReparaturPage
